import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { supabase } from "../../lib/supabase_client";
import '../../styles/page-base.css';

interface Referral {
  id: string;
  date: string;
  company_name: string;
  referred_org_name: string;
  product_name: string; 
  credits_earned: number;
  is_paid: boolean;
  created_at: string;
}

export default function AdminReferralPage() {  
  const [searchTerm, setSearchTerm] = useState("");
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      
      const {data: referralsData, error: referralError} = await supabase
        .rpc('get_referral_earnings');

      if (referralError) {
        console.error('Error fetching referrals:', referralError);
        return;
      }

      // Transform the data (much simpler now!)
      const transformedData = (referralsData || []).map((item: any) => ({
        id: item.id,
        date: new Date(item.created_at).toLocaleString(),
        company_name: item.referrer_name || '-',
        referred_org_name: item.referred_org_name || '-',
        product_name: item.product_name || '-',
        credits_earned: item.credits_earned || 0,
        is_paid: item.is_paid || false,
        created_at: item.created_at
      }));

      setReferrals(transformedData);
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const filteredReferrals = referrals.filter((referral) => {
    const matchesSearch = referral.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.referred_org_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });
  
  const handleDownloadReferrals = () => {
    const headers = ['Date', 'Product', 'Purchase By', 'Credits Gained By', 'Credits Earned', 'Status'];
    const csvData = [
      headers.join(','),
      ...filteredReferrals.map(referral => [
        `"${referral.date}"`,
        `"${referral.product_name}"`,
        `"${referral.referred_org_name}"`,
        `"${referral.company_name}"`,
        referral.credits_earned,
        referral.is_paid ? 'Paid' : 'Unpaid'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleInvite = () => {
    // TODO: Implement add customer functionality
    alert('Invite - Feature coming soon');
  };

  const totalPages = Math.ceil(filteredReferrals.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentReferrals = filteredReferrals.slice(startIndex, endIndex);  

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
            
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
            <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">Referrals</h1>
            </div>
            <div className="page-header-right">
              <button onClick={handleInvite} className="f-btn f-btn-primary">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Invite</span>
              </button>
              <button onClick={handleDownloadReferrals}
                className="f-btn f-btn-success">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export Excel</span>
              </button>
            </div>
          </div>

          <div className="page-card">
          <div className="flex items-center justify-between mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by Company Name, Product, or Credits Earned"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="page-table">
            <thead className="page-table-thead">
              <tr className="page-table-tr">
                <th className="page-table-th">Date</th>  
                <th className="page-table-th">Product</th>
                <th className="page-table-th">Purchase By</th>  
                <th className="page-table-th">Credits Gained By</th>
                <th className="page-table-th">Credits Earned</th>
                <th className="page-table-th">Status</th>
              </tr>
            </thead>

            <tbody className="page-table-tbody">
              {loading ? (
                <tr className="page-table-tr">
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : currentReferrals.length === 0 ? (
                <tr className="page-table-tr">
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    No referrals found.
                  </td>
                </tr>
              ) : (
                currentReferrals.map((referral) => (
                  <tr className="page-table-tr"
                    key={referral.id}
                  >
                    <td className="page-table-td">
                      {(() => {
                        const dateObj = new Date(referral.created_at);
                        const dateStr = dateObj.toLocaleDateString('en-GB');
                        const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        });
                        return (
                          <div className="flex flex-col">
                            <span>{dateStr}</span>
                            <span className="text-gray-500 text-xs">{timeStr}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="page-table-td">
                      {referral.product_name}
                    </td>
                    <td className="page-table-td">
                      {referral.referred_org_name}
                    </td>
                    <td className="page-table-td">
                      {referral.company_name}
                    </td>
                    <td className="page-table-td">
                      {referral.credits_earned}
                    </td>
                    <td className="page-table-td">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          referral.is_paid 
                            ? "bg-green-100 text-green-700" 
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {referral.is_paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredReferrals.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
            <div className="flex items-center space-x-2">
              <span>Rows per page:</span>
              <select 
                value={rowsPerPage} 
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <span>
                {startIndex + 1}-{Math.min(endIndex, filteredReferrals.length)} of {filteredReferrals.length}
            </span>
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
              >
                &lt;
              </button>
              <button
                className="f-btn f-btn-primary f-btn-xs"
              >
                {currentPage}
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
        </div>
        </div>
      </div>
    </MainLayout>
  );
}